import type {
  CardPolicy,
  CardUse,
  DesktopPolicyBody,
  DesktopPolicyState,
  OffWindow,
} from './desktop-types';

/** One card in the editor: its use and its own off times. */
export interface CardDraft {
  uuid: string;
  name: string;
  use: CardUse;
  off: OffWindow[];
}

/**
 * The Cards and schedule editor's working copy: pause, each card's use, and each card's own off times. `same` ("Same
 * schedule for every card") keeps every card on a schedule holding the same off times: it starts ticked only when they
 * already do (or one card is on a schedule), ticking copies the first scheduled card's to the others, and while it is
 * ticked an edit goes to all of them. A save sends each card's own; nothing is shared behind the screen.
 */
export interface PolicyDraft {
  paused: boolean;
  same: boolean;
  cards: CardDraft[];
  /** The policy's cards the machine no longer shows (a card pulled): kept as they are, so a save does not drop them. */
  away: CardPolicy[];
}

/** A new off time: weekdays, 9 to 5. */
export const NEW_WINDOW: OffWindow = {
  days: ['mon', 'tue', 'wed', 'thu', 'fri'],
  from: '09:00',
  to: '17:00',
};

const sameTimes = (a: OffWindow[], b: OffWindow[]) => JSON.stringify(a) === JSON.stringify(b);

/** The cards on a schedule, in the machine's order. */
export const scheduledOf = (d: PolicyDraft): CardDraft[] =>
  d.cards.filter((c) => c.use === 'schedule');

/** Whether every card on a schedule has the same off times (true for none or one). */
export function allSame(cards: CardDraft[]): boolean {
  const scheduled = cards.filter((c) => c.use === 'schedule');
  return scheduled.every((c) => sameTimes(c.off, scheduled[0]?.off ?? []));
}

/** The editor's copy of a machine's policy: every card it shows (by UUID), each from the policy or Always. */
export function draftOf(state: DesktopPolicyState): PolicyDraft {
  const listed = state.policy?.cards ?? [];
  const cards: CardDraft[] = state.cards
    .filter((c): c is typeof c & { uuid: string } => c.uuid !== null)
    .map((c) => {
      const p = listed.find((l) => l.uuid.toLowerCase() === c.uuid.toLowerCase());
      return { uuid: c.uuid, name: c.name, use: p?.use ?? 'always', off: p?.off ?? [] };
    });
  const shown = new Set(cards.map((c) => c.uuid.toLowerCase()));
  return {
    paused: state.policy?.paused ?? false,
    same: allSame(cards),
    cards,
    away: listed.filter((l) => !shown.has(l.uuid.toLowerCase())),
  };
}

/** The save's body: each card's own off times (a card Always or Never keeps none). */
export function bodyOf(d: PolicyDraft, basedOn: string | null): DesktopPolicyBody {
  const cards: CardPolicy[] = d.cards.map((c) => ({
    uuid: c.uuid,
    name: c.name,
    use: c.use,
    off: c.use === 'schedule' ? c.off : [],
  }));
  return { paused: d.paused, cards: [...cards, ...d.away], basedOn };
}

const TIME = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

/** What stops a save, in words; empty when it can go. */
export function problemsOf(d: PolicyDraft): string[] {
  const windows = scheduledOf(d).flatMap((c) => c.off);
  const out: string[] = [];
  if (windows.some((w) => w.days.length === 0))
    out.push('Pick at least one day for each off time.');
  if (windows.some((w) => !TIME.test(w.from) || !TIME.test(w.to)))
    out.push('Give each off time a start and an end.');
  return out;
}

/**
 * The draft with one card's use changed. A card put on a schedule starts from weekdays 9 to 5 when it has no off times;
 * if that makes the cards on a schedule differ, "same" is cleared, so each card's own times show under it rather than
 * the card silently taking another's.
 */
export function withUse(d: PolicyDraft, uuid: string, use: CardUse): PolicyDraft {
  const cards = d.cards.map((c) =>
    c.uuid !== uuid
      ? c
      : { ...c, use, off: use === 'schedule' && c.off.length === 0 ? [NEW_WINDOW] : c.off },
  );
  return { ...d, cards, same: d.same && allSame(cards) };
}

/** The draft with off times replaced: every scheduled card's while "same" is ticked (uuid null), else one card's. */
export function withTimes(d: PolicyDraft, uuid: string | null, off: OffWindow[]): PolicyDraft {
  return {
    ...d,
    cards: d.cards.map((c) =>
      (uuid === null ? c.use === 'schedule' : c.uuid === uuid) ? { ...c, off } : c,
    ),
  };
}

/** Ticking copies the first scheduled card's off times to the others; unticking leaves each card its copy. */
export function withSame(d: PolicyDraft, same: boolean): PolicyDraft {
  const first = scheduledOf(d)[0]?.off ?? [];
  return same ? withTimes({ ...d, same }, null, first) : { ...d, same };
}

/** Whether two drafts would save the same settings (pause, each card's use and its off times; "same" is only a view). */
export function sameSettings(a: PolicyDraft, b: PolicyDraft): boolean {
  const settings = (d: PolicyDraft) => {
    const { paused, cards } = bodyOf(d, null);
    return JSON.stringify({ paused, cards });
  };
  return settings(a) === settings(b);
}
